import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { markdownToHtml, htmlToMarkdown, normalizeMarkdownValue } from './markdownUtils';
import Toolbar from './Toolbar';

const EditorWrapper = styled.div`
  width: 100%;
  font-family: inherit;

  .rich-text-editor-box {
    border: 1px solid ${({ theme }) => theme.colors?.grey3 || '#d3d3d3'};
    border-radius: 4px;
    background: ${({ theme }) => theme.colors?.white || '#fff'};
  }

  .editor-content {
    height: 130px;
    overflow-y: auto;
    padding: 12px 15px;
    font-size: 14px;

    .ProseMirror {
      outline: none;
      min-height: 130px;
    }

    .ProseMirror p.is-editor-empty:first-child::before {
      content: attr(data-placeholder);
      color: ${({ theme }) => theme.colors?.grey3 || '#A7AEBC'};
      pointer-events: none;
    }

    .ProseMirror:focus p.is-editor-empty:first-child::before {
      content: none;
    }

    .ProseMirror pre {
      background: ${({ theme }) => theme.colors.grey1};
      border-radius: 4px;
      padding: 12px;
      font-family: monospace;
      font-size: 13px;
    }

    .ProseMirror blockquote {
      margin: 0 0 12px;
      padding-left: 12px;
      border-left: 3px solid ${({ theme }) => theme.colors.grey3};
      color: ${({ theme }) => theme.colors.grey4};
    }

    .ProseMirror h1,
    .ProseMirror h2,
    .ProseMirror h3 {
      margin: 0 0 12px;
      font-weight: 600;
      line-height: 1.3;
    }

    .ProseMirror h1 {
      font-size: 28px;
    }

    .ProseMirror h2 {
      font-size: 24px;
    }

    .ProseMirror h3 {
      font-size: 20px;
    }

    .ProseMirror > *:last-child {
      margin-bottom: 0;
    }
  }
`;

const RichTextEditor = ({
  value = '',
  onChange,
  placeholder = '',
  autoFocus = false,
  editorClassName = '',
  $isRtl = false
}) => {
  const lastEmittedMarkdownRef = useRef(normalizeMarkdownValue(value));
  const linkMenuInstanceRef = useRef(0);
  const [linkMenu, setLinkMenu] = useState(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3]
        },
        blockquote: true,
        strike: false,
        code: false
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: null,
          rel: null,
          class: null
        }
      }),
      Placeholder.configure({ placeholder })
    ],
    content: markdownToHtml(value),
    editorProps: {
      attributes: {
        'data-placeholder': placeholder
      }
    },
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      const markdown = htmlToMarkdown(html);
      lastEmittedMarkdownRef.current = normalizeMarkdownValue(markdown);
      onChange(markdown);
    }
  });

  // Keep editor in sync when `value` changes from outside (skip when change came from our own onChange).
  useEffect(() => {
    if (!editor) return;
    const fromParent = normalizeMarkdownValue(value);
    if (fromParent !== lastEmittedMarkdownRef.current) {
      lastEmittedMarkdownRef.current = fromParent;
      editor.commands.setContent(markdownToHtml(value), false);
    }
  }, [value, editor]);

  useEffect(() => {
    if (!editor || !autoFocus) return;
    editor.commands.focus('start');
  }, [editor, autoFocus]);

  useEffect(() => {
    if (!editor) return;
    const onFocus = () => {
      if (editor.isEmpty) editor.commands.focus('start');
    };
    editor.on('focus', onFocus);
    return () => editor.off('focus', onFocus);
  }, [editor]);

  const closeLinkMenu = useCallback(() => setLinkMenu(null), []);

  const openLinkMenu = useCallback(() => {
    if (!editor) return;
    linkMenuInstanceRef.current += 1;
    setLinkMenu({
      initialUrl: editor.getAttributes('link')?.href || '',
      instanceKey: linkMenuInstanceRef.current
    });
  }, [editor]);

  const handleLinkDone = useCallback(
    ({ url }) => {
      if (!editor || !url) return;
      editor.chain().focus().extendMarkRange('link').setLink({ href: url, target: null, rel: null }).run();
      closeLinkMenu();
    },
    [closeLinkMenu, editor]
  );

  if (!editor) return null;

  return (
    <EditorWrapper $isRtl={$isRtl} className={editorClassName}>
      <div className="rich-text-editor-box">
        <Toolbar
          editor={editor}
          linkMenu={linkMenu}
          onOpenLinkMenu={openLinkMenu}
          onCloseLinkMenu={closeLinkMenu}
          onLinkDone={handleLinkDone}
        />
        <div className="editor-content">
          <EditorContent editor={editor} />
        </div>
      </div>
    </EditorWrapper>
  );
};

export default RichTextEditor;
